import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { addItemToCart, removeItemFromCart, updateCartItemOnBackend, clearCartOnBackend, getCartFromBackend, getProductDataFromCartItem, removeCoupon } from '../services/cartService'
import { toast } from 'react-toastify'
import ConfirmationModal from '../components/ConfirmationModal'
import { diffSeenPrices, mergePriceChanges, priceKey } from '../utils/cartIntegrity'

const invalidateCartCache = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'INVALIDATE_CART_CACHE' })
  }
}

const CART_KEY = ['cart']
const ANON_CART_STORAGE_KEY = 'anon_cart'
// The unit price the customer last saw for each cart line (server prices only).
const SEEN_PRICES_KEY = 'cart_seen_prices'

const readSeenPrices = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SEEN_PRICES_KEY) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}
const writeSeenPrices = seen => {
  try {
    if (Object.keys(seen).length) localStorage.setItem(SEEN_PRICES_KEY, JSON.stringify(seen))
    else localStorage.removeItem(SEEN_PRICES_KEY)
  } catch {}
}

const isAuth = () => !!localStorage.getItem('authToken') || !!localStorage.getItem('token') || !!localStorage.getItem('access_token')

const saveAnonCart = (cartData) => {
  try {
    localStorage.setItem(ANON_CART_STORAGE_KEY, JSON.stringify(cartData))
  } catch {}
}

const loadAnonCart = () => {
  try {
    const raw = localStorage.getItem(ANON_CART_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && Array.isArray(parsed.items)) return parsed
  } catch {}
  return null
}


const CartContext = createContext()

export const CartProvider = ({ children }) => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [pendingProduct, setPendingProduct] = useState(null)
  const [conflictMessage, setConflictMessage] = useState('')
  const [cartWasCleared, setCartWasCleared] = useState(false)

  const {
    data: cartData,
    isLoading: queryLoading,
    isFetching,
    isFetched: isInitialized
  } = useQuery({
    queryKey: CART_KEY,
    queryFn: async () => {
      if (!isAuth()) {
        const saved = loadAnonCart()
        return saved || { cart: null, items: [] }
      }
      const freshCart = await getCartFromBackend()
      return { cart: freshCart, items: freshCart?.items || [] }
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1
  })

  // Anonymous users: never show loading since frontend is the source of truth
  const isLoading = isAuth() ? (queryLoading || isFetching) : false

  const cart = cartData?.cart || null
  // Stable between renders, so the cart callbacks below do not change (and
  // re-render every consumer) each time the provider renders.
  const rawItems = cartData?.items
  const items = useMemo(() => rawItems || [], [rawItems])

  // The server re-prices every line on each read. When a line's price differs
  // from what this customer last saw (the vendor changed it, an offer ended or
  // began), say so instead of letting the total move silently.
  const [pendingPriceChanges, setPendingPriceChanges] = useState([])
  useEffect(() => {
    // Only a cart the server has answered with: an empty list while it loads
    // would otherwise "forget" every remembered price.
    if (!isAuth() || !cartData) return
    const { changes, seen } = diffSeenPrices(readSeenPrices(), items)
    writeSeenPrices(seen)
    if (changes.length) setPendingPriceChanges(prev => mergePriceChanges(prev, changes))
  }, [items, cartData])
  const priceChanges = useMemo(() => {
    const keys = new Set(items.map(priceKey))
    return pendingPriceChanges.filter(c => keys.has(c.key))
  }, [pendingPriceChanges, items])
  const acknowledgePriceChanges = useCallback(() => setPendingPriceChanges([]), [])

  const setCartState = useCallback(
    (newCart, newItems) => {
      const data = { cart: newCart, items: newItems }
      queryClient.setQueryData(CART_KEY, data)
      if (!isAuth()) saveAnonCart(data)
    },
    [queryClient]
  )

  const refetchCart = useCallback(async () => {
    if (!isAuth()) return cart
    const freshCart = await getCartFromBackend()
    if (freshCart) {
      setCartState(freshCart, freshCart.items || [])
      invalidateCartCache()
    }
    return freshCart
  }, [setCartState, cart])

  const addToCart = useCallback(
    async (product, special_instructions = '', quantity = 1, variantId = null) => {
      if (!product || !product.id) {
        toast.error('Invalid product data')
        return
      }
      const existingItem = items.find(i => i.product?.id === product.id)
      const effectiveVariantId = variantId != null ? variantId : (existingItem?.variant_id ?? existingItem?.product?.variant_id ?? null)
      const maxOrderQuantity = product.max_order_quantity || 999
      const currentQuantity = existingItem?.quantity || 0
      if (currentQuantity + quantity > maxOrderQuantity) {
        toast.info(`The max order quantity for this product is ${maxOrderQuantity}`)
        return
      }

      const prevCart = cart
      const prevItems = items

      let optimisticItems
      if (existingItem) {
        optimisticItems = prevItems.map(item => {
          if (item.id === existingItem.id) {
            const unitPrice = item.unit_price || item.price || product.price || 0
            const newQuantity = item.quantity + quantity
            return { ...item, quantity: newQuantity, unit_price: unitPrice, total_price: unitPrice * newQuantity, special_instructions: special_instructions || item.special_instructions, isOptimistic: true }
          }
          return item
        })
      } else {
        optimisticItems = [...prevItems, { id: `optimistic-${Date.now()}`, product: { ...product }, quantity, unit_price: product.price || 0, total_price: (product.price || 0) * quantity, special_instructions, variant_id: effectiveVariantId, isOptimistic: true }]
      }
      const optimisticTotal = optimisticItems.reduce((sum, it) => sum + (Number(it.total_price) || 0), 0)
      const optimisticCart = prevCart ? { ...prevCart, total_amount: optimisticTotal } : { id: `optimistic-cart-${Date.now()}`, total_amount: optimisticTotal }
      setCartState(optimisticCart, optimisticItems)

      try {
        const result = await addItemToCart({ productId: product.id, quantity, specialInstructions: special_instructions, variantId: effectiveVariantId })

        if (result?.grocery_store_notice && result.grocery_store_notice.can_order && !result.grocery_store_notice.is_open) {
          const notice = result.grocery_store_notice
          toast.info(notice.order_note || `${notice.vendor_name} is closed. Your order will be processed when they open.`, { autoClose: 5000, icon: '🛒' })
        }

        // Anonymous users: frontend is the source of truth — keep the optimistic state
        if (!isAuth()) {
          invalidateCartCache()
          return
        }

        if (result?.cart) {
          if (cartWasCleared && result.cart.applied_promo_code) {
            try {
              await removeCoupon()
              await refetchCart()
              setCartWasCleared(false)
              return
            } catch (error) {
              console.warn('Failed to remove persisted promo code:', error)
            }
          }
          if (cartWasCleared) setCartWasCleared(false)
          setCartState(result.cart, result.cart.items || [])
          invalidateCartCache()
          return
        }

        const freshCart = await getCartFromBackend()
        if (cartWasCleared && freshCart?.applied_promo_code) {
          try {
            await removeCoupon()
            await refetchCart()
            setCartWasCleared(false)
            return
          } catch (error) {
            console.warn('Failed to remove persisted promo code:', error)
          }
        }
        if (cartWasCleared) setCartWasCleared(false)

        if (freshCart && freshCart.items) {
          setCartState(freshCart, freshCart.items)
          invalidateCartCache()
        } else if (result?.success && result.cart_item) {
          let itemsAfterServer
          if (existingItem) {
            itemsAfterServer = optimisticItems.map(it => (it.id === existingItem.id || it.product?.id === product.id ? { ...result.cart_item } : it))
          } else {
            const placeholder = optimisticItems.find(it => it.isOptimistic && it.product?.id === product.id)
            itemsAfterServer = optimisticItems.map(it => (it === placeholder ? { ...result.cart_item } : it))
          }
          const finalCart = prevCart ? { ...prevCart, total_amount: result.cart_total } : { id: prevCart?.id || `cart-${Date.now()}`, total_amount: result.cart_total }
          setCartState(finalCart, itemsAfterServer)
          invalidateCartCache()
        }
      } catch (error) {
        setCartState(prevCart, prevItems)
        if (error.response?.status === 409 && error.response.data?.vendorConflict) {
          setPendingProduct({ product, quantity, special_instructions, variantId })
          setConflictMessage(error.response.data.message)
          setShowModal(true)
        } else {
          console.error('Error adding item to cart (rolled back):', error)
          toast.error(error.response?.data?.error || 'Failed to add item to cart')
        }
      }
    },
    [cart, items, cartWasCleared, setCartState, refetchCart]
  )

  const handleConfirmReplace = useCallback(async () => {
    setShowModal(false)
    const { product, quantity, special_instructions, variantId } = pendingProduct
    const prevCart = cart
    const prevItems = items
    const optimisticItem = { id: `optimistic-replace-${Date.now()}`, product: { ...product }, quantity, unit_price: product.price || 0, total_price: (product.price || 0) * quantity, special_instructions, variant_id: variantId || null, isOptimistic: true }
    const optimisticCart = { id: `optimistic-cart-${Date.now()}`, total_amount: optimisticItem.total_price }
    setCartState(optimisticCart, [optimisticItem])

    try {
      const result = await addItemToCart({ productId: product.id, quantity, specialInstructions: special_instructions, variantId, force: true })

      if (!isAuth()) {
        invalidateCartCache()
        return
      }

      if (result?.cart) {
        setCartState(result.cart, result.cart.items || [])
        return
      }
      const freshCart = await getCartFromBackend()
      if (freshCart && freshCart.items) {
        setCartState(freshCart, freshCart.items)
      } else if (result?.success) {
        const finalCart = { id: optimisticCart.id, total_amount: result.cart_total }
        setCartState(finalCart, [result.cart_item])
      }
    } catch (retryError) {
      setCartState(prevCart, prevItems)
      console.error('Failed to replace cart (rolled back):', retryError)
      toast.error('Failed to replace cart')
    }
  }, [pendingProduct, cart, items, setCartState])

  const removeFromCart = useCallback(
    async identifier => {
      if (!identifier) return
      const itemById = items.find(it => it.id === identifier)
      const item = itemById || items.find(it => it.product?.id === identifier)
      if (!item) {
        toast.error('Item not found in cart')
        return
      }
      const itemId = item.id
      const prevCart = cart
      const prevItems = items
      const optimisticItems = prevItems.filter(it => it.id !== itemId)
      const optimisticTotal = optimisticItems.reduce((s, it) => s + (Number(it.total_price) || 0), 0)
      const optimisticCart = prevCart ? { ...prevCart, total_amount: optimisticTotal } : null
      setCartState(optimisticCart, optimisticItems)

      try {
        if (!isAuth()) {
          invalidateCartCache()
          return
        }

        const result = await removeItemFromCart(itemId)
        if (result?.cart_total !== undefined && itemId.toString().startsWith('temp-')) {
          await refetchCart()
          return
        }
        const freshCart = await getCartFromBackend()
        if (freshCart) {
          setCartState(freshCart, freshCart.items || [])
          invalidateCartCache()
        } else {
          const finalCart = optimisticCart ? { ...optimisticCart, total_amount: result.cart_total } : { id: prevCart?.id || `cart-${Date.now()}`, total_amount: result.cart_total }
          setCartState(finalCart, optimisticItems)
          invalidateCartCache()
        }
      } catch (error) {
        setCartState(prevCart, prevItems)
        console.error('Error removing item from cart (rolled back):', error)
        toast.error(error.response?.data?.error || 'Failed to remove item')
      }
    },
    [cart, items, setCartState, refetchCart]
  )

  const clearCart = useCallback(async () => {
    const prevCart = cart
    const prevItems = items
    setCartState(null, [])
    setCartWasCleared(true)

    try {
      if (!isAuth()) {
        invalidateCartCache()
        toast.success('Cart cleared successfully')
        return
      }

      if (cart?.applied_promo_code) {
        try {
          await removeCoupon()
        } catch (error) {
          console.warn('Failed to remove promo code during cart clear:', error)
        }
      }
      await clearCartOnBackend()
      const freshCart = await getCartFromBackend()
      setCartState(freshCart, freshCart?.items || [])
      invalidateCartCache()
      toast.success('Cart cleared successfully')
    } catch (error) {
      setCartState(prevCart, prevItems)
      console.error('Error clearing cart (rolled back):', error)
      toast.error(error.response?.data?.error || 'Failed to clear cart')
    }
  }, [cart, items, setCartState])

  const updateItemQuantity = useCallback(
    async (...args) => {
      let itemId, quantity, special_instructions, variantId
      let productObj = null
      if (typeof args[0] === 'object') {
        productObj = args[0]
        const productId = args[1]
        quantity = args[2]
        special_instructions = args[3]
        variantId = args[4]
        const found = items.find(it => it.product?.id === productId)
        itemId = found?.id
      } else {
        itemId = args[0]
        quantity = args[1]
        special_instructions = args[2]
        variantId = args[3]
      }
      if (!itemId) {
        toast.error('Item not found')
        return
      }
      if (quantity === 0) return removeFromCart(itemId)

      const existingItem = items.find(it => it.id === itemId)
      const productData = productObj || existingItem?.product || getProductDataFromCartItem(existingItem, existingItem?.product?.id)
      const maxQuantity = productData.max_order_quantity || 999
      const currentQuantity = existingItem?.quantity || 0
      if (quantity > maxQuantity && quantity > currentQuantity) {
        toast.info(`The max order quantity for this product is ${maxQuantity}`)
        return
      }

      const prevCart = cart
      const prevItems = items
      const optimisticItems = prevItems.map(it => {
        if (it.id === itemId) {
          const unitPrice = it.unit_price || it.price || productData.price || 0
          return { ...it, quantity, unit_price: unitPrice, total_price: unitPrice * quantity, special_instructions: special_instructions !== undefined ? special_instructions : it.special_instructions, variant_id: variantId !== undefined ? variantId : it.variant_id, isOptimisticUpdate: true }
        }
        return it
      })
      const optimisticTotal = optimisticItems.reduce((s, it) => s + (Number(it.total_price) || 0), 0)
      const optimisticCart = prevCart ? { ...prevCart, total_amount: optimisticTotal } : null
      setCartState(optimisticCart, optimisticItems)

      try {
        if (!isAuth()) {
          invalidateCartCache()
          return
        }

        const result = await updateCartItemOnBackend(itemId, { quantity, variantId, specialInstructions: special_instructions })
        if (result?.cart) {
          setCartState(result.cart, result.cart.items || [])
          invalidateCartCache()
          return
        }
        if (result?.success && result.cart_item) {
          const finalItems = optimisticItems.map(it => {
            if (it.id !== itemId) return it
            const serverItem = result.cart_item
            return { ...it, ...serverItem, variant: serverItem.variant || it.variant || null, variant_id: serverItem.variant_id ?? it.variant_id ?? (serverItem.variant?.id || it.variant?.id) ?? null }
          })
          const finalCart = prevCart ? { ...prevCart, total_amount: result.cart_total } : { id: prevCart?.id || `cart-${Date.now()}`, total_amount: result.cart_total }
          setCartState(finalCart, finalItems)
          invalidateCartCache()
          return
        }
        const freshCart = await getCartFromBackend()
        if (freshCart && freshCart.items) {
          setCartState(freshCart, freshCart.items)
          invalidateCartCache()
        }
      } catch (error) {
        setCartState(prevCart, prevItems)
        console.error('Error updating quantity (rolled back):', error)
        toast.error(error.response?.data?.error || 'Failed to update cart')
      }
    },
    [cart, items, setCartState, removeFromCart]
  )

  const refreshCart = useCallback(async () => {
    try {
      await refetchCart()
    } catch (error) {
      console.error('Error refreshing cart:', error)
    }
  }, [refetchCart])

  const removeAppliedCoupon = useCallback(async () => {
    if (!cart?.applied_promo_code) return
    const prevCart = cart
    const prevItems = items
    const discount = Number(prevCart.promo_discount_amount) || 0
    const optimisticCart = {
      ...prevCart,
      applied_promo_code: null,
      promo_code_info: null,
      promo_discount_amount: null,
      total_amount: discount > 0 && prevCart.total_amount != null ? Number(prevCart.total_amount) + discount : prevCart.total_amount
    }
    setCartState(optimisticCart, prevItems)

    try {
      const result = await removeCoupon()
      if (!result?.success) {
        setCartState(prevCart, prevItems)
        toast.error(result?.message || 'Failed to remove promo code')
        return
      }
      await refetchCart()
    } catch (error) {
      setCartState(prevCart, prevItems)
      console.error('Error removing promo code (rolled back):', error)
      toast.error(error.response?.data?.error || 'Failed to remove promo code')
    }
  }, [cart, items, setCartState, refetchCart])

  return (
    <CartContext.Provider
      value={{
        cart,
        items,
        addToCart,
        removeFromCart,
        updateItemQuantity,
        clearCart,
        refreshCart,
        removeAppliedCoupon,
        isLoading,
        isInitialized,
        priceChanges,
        acknowledgePriceChanges
      }}
    >
      {children}
      <ConfirmationModal isOpen={showModal} message={conflictMessage} onConfirm={handleConfirmReplace} onCancel={() => setShowModal(false)} />
    </CartContext.Provider>
  )
}

export const useCart = () => useContext(CartContext)
